import { Injectable } from '@angular/core';
import { ShapeState } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { Output } from '../../../../team-apis/otterdam/outputs/output';
import { getOutputByName } from '../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceState } from '../../../h3ml/state/task-workspace-state';
import { getDiff } from '../../../production-workspace/submissions/get-diff';
import {
  getChangedFrameNumbers,
  getShape,
} from '../../../production-workspace/submissions/persistence-manager-helper';
import { getInternalToExternalConverter } from '../../../shared/internal-external-converters/internal-to-external-shape-converter';
import { AbstractChangeOperationService } from './abstract-change-operation.service';
import { createDeleteShapeLocationBuilders } from './operation-builders/delete-shape-location-operation-builder';
import { createDeleteShapeOperationBuilders } from './operation-builders/delete-shape-operation-builder';
import {
  ShapeLocationOperationBuilderParams,
  ShapeLocationParams,
} from './operation-builders/shape-operation-builder-params';
import { createUpsertShapeLocationBuilders } from './operation-builders/upsert-shape-location-operation-builder';
import { UpsertShapeOperationBuilder } from './operation-builders/upsert-shape-operation-builder';

@Injectable()
export class ShapeChangeOperationService extends AbstractChangeOperationService<ShapeLocationOperationBuilderParams> {
  protected override getEntries(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    const shapeDiff = this.getShapeDiff(previous, current);

    return shapeDiff.map((shapeEntry) => {
      const currentShape = getShape(current, shapeEntry);
      const previousShape = getShape(previous, shapeEntry);
      const output = getOutputByName(current.project, shapeEntry.outputName);
      const builderParams: ShapeLocationOperationBuilderParams = {
        currentShape,
        previousShape,
        output,
        locations: this.getChangedLocations(
          output,
          previousShape,
          currentShape,
        ),
      };
      return builderParams;
    });
  }

  protected override createBuilders(
    entry: ShapeLocationOperationBuilderParams,
  ) {
    return [
      new UpsertShapeOperationBuilder(entry),
      ...createDeleteShapeOperationBuilders(entry),
      ...createDeleteShapeLocationBuilders(entry),
      ...createUpsertShapeLocationBuilders(entry),
    ];
  }

  private getShapeDiff(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    return getDiff(previous, current, {
      selectors: [
        {
          getNextLevel: (state) => state.outputAnnotationInfo,
          keyedBy: 'outputName',
        },
        {
          // @ts-ignore -- fix me
          getNextLevel: (shapeInfoForOutput) => shapeInfoForOutput.shapes,
          keyedBy: 'shapeId',
        },
      ],
    });
  }

  private getChangedLocations(
    output: Output,
    previousShape?: ShapeState,
    currentShape?: ShapeState,
  ) {
    if (!currentShape) {
      return [];
    }
    const frameNumbers = getChangedFrameNumbers(previousShape, currentShape);
    const converter = getInternalToExternalConverter(output);

    return frameNumbers.map((frameNumber) => {
      const location: ShapeLocationParams = {
        frameNumber,
        operationAttributes: converter.convertToLocationOperationAttributes(
          currentShape,
          frameNumber,
        ),
      };
      return location;
    });
  }
}
